/** Typed client for CNP Hub API (hand-maintained from OpenAPI surface). */

export type AuthTokenResponse = {
  accessToken: string;
  userUuid: string;
  email?: string | null;
};

export type PreferenceResponse = {
  sourceKey: string;
  sourceName: string;
  typeKey: string;
  typeDisplayName: string;
  category: string;
  channel: string;
  optedIn: boolean;
};

export type SubscriptionResponse = {
  subscriptionId: number;
  endpoint: string;
  platform: string;
  origin: string;
  deviceLabel?: string | null;
  deviceInfoJson?: string | null;
  isActive: boolean;
  lastSeenAt?: string | null;
  createdAt: string;
};

export type MeResponse = {
  userUuid: string;
  email?: string | null;
  displayName?: string | null;
};

export type VapidPublicKeyResponse = {
  publicKey: string;
  subject: string;
};

export type EmitNotificationResponse = {
  enqueued: number;
  message?: string | null;
};

export type RegisterSubscriptionBody = {
  channel: string;
  endpoint: string;
  keys: { p256dh: string; auth: string };
  origin: string;
  platform: string;
  deviceLabel?: string;
  deviceInfo?: Record<string, unknown>;
};

const TOKEN_KEY = "cnp.accessToken";

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  auth = true
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (auth) {
    const token = getStoredToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(path, { ...init, headers });
  if (response.status === 204 || response.status === 202) {
    if (response.headers.get("content-type")?.includes("application/json")) {
      return (await response.json()) as T;
    }
    return undefined as T;
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.error ?? body.title ?? detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  register: (email?: string, displayName?: string) =>
    request<AuthTokenResponse>("/api/v1/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, displayName })
    }, false),

  authStart: (email: string) =>
    request<void>("/api/v1/auth/start", {
      method: "POST",
      body: JSON.stringify({ email })
    }, false),

  authVerify: (email: string, code: string, displayName?: string) =>
    request<AuthTokenResponse>("/api/v1/auth/verify", {
      method: "POST",
      body: JSON.stringify({ email, code, displayName })
    }, false),

  me: () => request<MeResponse>("/api/v1/auth/me"),

  getVapidPublicKey: () =>
    request<VapidPublicKeyResponse>("/api/v1/config/vapid-public-key", {}, false),

  listPreferences: () => request<PreferenceResponse[]>("/api/v1/preferences"),

  setPreference: (source: string, type: string, channel: string, optedIn: boolean) =>
    request<void>("/api/v1/preferences", {
      method: "PUT",
      body: JSON.stringify({ source, type, channel, optedIn })
    }),

  registerSubscription: (body: RegisterSubscriptionBody) =>
    request<SubscriptionResponse>("/api/v1/subscriptions", {
      method: "POST",
      body: JSON.stringify(body)
    }),

  listSubscriptions: () => request<SubscriptionResponse[]>("/api/v1/subscriptions"),

  deactivateDevice: (deviceId: number) =>
    request<void>(`/api/v1/devices/${deviceId}/deactivate`, { method: "POST" }),

  activateDevice: (deviceId: number) =>
    request<void>(`/api/v1/devices/${deviceId}/activate`, { method: "POST" }),

  testSend: (title?: string, body?: string, url?: string) =>
    request<EmitNotificationResponse>("/api/v1/dev/test-send", {
      method: "POST",
      body: JSON.stringify({ title, body, url })
    })
};
